import Phaser from 'phaser';
import { SCENE_KEYS, ZONE_NAMES } from '../data/gameConstants';
import { NPC } from '../objects/NPC';
import { SaveSystem } from '../systems/SaveSystem';
import { ZoneCoreSystems } from '../systems/ZoneCoreSystems';
import { DecisionPanel } from '../ui/DecisionPanel';
import { ObjectiveHint } from '../ui/ObjectiveHint';
import { QuestPanel } from '../ui/QuestPanel';
import { addZoneHudHeader } from '../ui/ZoneHudHeader';
import { addZoneMenuButton } from '../ui/zoneMenuButton';
import { SceneTransition } from '../utils/SceneTransition';
import { Zone5EscortSystem } from '../zones/zone5/Zone5EscortSystem';
import { ZONE5_POSITIONS, Zone5Map } from '../zones/zone5/Zone5Map';
import { Zone5QuestFlow } from '../zones/zone5/Zone5QuestFlow';

export class Zone5RescueScene extends Phaser.Scene {
  private core?: ZoneCoreSystems;
  private questPanel?: QuestPanel;
  private questFlow?: Zone5QuestFlow;
  private objectiveHint?: ObjectiveHint;
  private decisionPanel?: DecisionPanel;
  private escortSystem?: Zone5EscortSystem;
  private lansia?: NPC;
  private ibuHamil?: NPC;

  constructor() {
    super({ key: 'Zone5RescueScene' });
  }

  create(): void {
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    const map = new Zone5Map(this);
    const obstacles = map.build();

    this.core = new ZoneCoreSystems(this);
    this.questPanel = new QuestPanel(this);
    this.objectiveHint = new ObjectiveHint(this);
    this.decisionPanel = new DecisionPanel(this);
    this.escortSystem = new Zone5EscortSystem(this, {
      x: ZONE5_POSITIONS.SAFE_ZONE.x,
      y: ZONE5_POSITIONS.SAFE_ZONE.y,
      radius: 95,
    });

    this.core.setQuestPanel(this.questPanel);
    this.core.setupHUD(5);
    this.core.setupMobileControls();
    const startPos = SaveSystem.getPlayerStartPosition(
      SCENE_KEYS.ZONE5,
      ZONE5_POSITIONS.SPAWN.x,
      ZONE5_POSITIONS.SPAWN.y,
    );
    SaveSystem.saveProgress(SCENE_KEYS.ZONE5, 5, startPos);
    this.core.initPlayer(startPos.x, startPos.y);
    this.core.setupObstacles(obstacles);

    this.lansia = new NPC(this, ZONE5_POSITIONS.LANSIA.x, ZONE5_POSITIONS.LANSIA.y, {
      id: 'zone5_lansia',
      name: 'Lansia',
      type: 'lansia',
      interactionRadius: 100,
      showLabel: false,
    });

    this.ibuHamil = new NPC(this, ZONE5_POSITIONS.IBU_HAMIL.x, ZONE5_POSITIONS.IBU_HAMIL.y, {
      id: 'zone5_ibu_hamil',
      name: 'Ibu Hamil',
      type: 'ibu_hamil',
      interactionRadius: 90,
      showLabel: false,
    });
    this.lansia.setInteractEnabled(false);
    this.ibuHamil.setInteractEnabled(false);

    const ibundo = new NPC(this, ZONE5_POSITIONS.IBUNDO.x, ZONE5_POSITIONS.IBUNDO.y, {
      id: 'zone5_ibundo',
      name: 'Ibundo Putri Rimas',
      type: 'ibundo',
      interactionRadius: 110,
      showLabel: false,
    });
    ibundo.setInteractEnabled(false);

    this.questFlow = new Zone5QuestFlow(
      this.core,
      map,
      this.lansia,
      this.ibuHamil,
      ibundo,
      this.objectiveHint,
      this.decisionPanel,
      this.escortSystem,
    );

    this.lansia.setOnInteract(() => this.questFlow!.onVulnerableInteract());
    this.ibuHamil.setOnInteract(() => this.questFlow!.onVulnerableInteract());
    ibundo.setOnInteract(() => this.questFlow!.onIbundoInteract());

    this.core.registerInteractables([this.lansia, this.ibuHamil, ibundo]);
    this.core.setInteractionGate(() => this.decisionPanel!.isVisible());
    this.buildUI();
    this.questFlow.start();
    SceneTransition.reveal(this);
  }

  private buildUI(): void {
    addZoneHudHeader(this, ZONE_NAMES.ZONE5, 'Aksi Sekundang Setungguan');
    addZoneMenuButton(this);
  }

  update(time: number, delta: number): void {
    this.questFlow?.prepareFrame();
    this.core?.update(time, delta);

    const player = this.core?.getPlayer();
    if (player && this.questFlow) {
      this.lansia?.updateLabels();
      this.ibuHamil?.updateLabels();
      this.questFlow.update(player.x, player.y, delta);
    }
  }

  shutdown(): void {
    this.questFlow?.destroy();
    this.core?.destroy();
    this.questFlow = undefined;
    this.core = undefined;
    this.questPanel = undefined;
  }
}
